import { Component, OnInit, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BaseComponent } from 'src/app/core/infrastructure/classes/base-component';
import {
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MetaDataField } from 'src/app/core/infrastructure/classes/meta-data-field';
import { IFieldValidator } from 'src/app/core/infrastructure/interfaces/i-field-validator';
import { FieldValidatorType } from 'src/app/core/infrastructure/enums/field-validator-type.enum';
import { MinMaxValidator } from 'src/app/core/infrastructure/classes/metadataDisplayControls/validators/min-max-validator';
import { CustomPatternValidator } from 'src/app/core/infrastructure/classes/metadataDisplayControls/validators/custom-pattern-validator';
import { LessMoreValidator } from 'src/app/core/infrastructure/classes/metadataDisplayControls/validators/less-more-validator';
import { DisplayControlInput } from 'src/app/core/infrastructure/enums/display-control-input.enum';
import { LookUpDisplayControl } from 'src/app/core/infrastructure/classes/metadataDisplayControls/controls/look-up-display-control';
import { LookUpMappingType } from 'src/app/core/infrastructure/enums/look-up-mapping-type.enum';
import { ResourceService } from 'src/app/core/services/resource-service';
import { SnotifyService } from 'ng-snotify';
import { asapScheduler, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { RestockSuggestionService } from 'src/app/core/services/restock-suggestion.service';
import { ItemField } from 'src/app/core/infrastructure/enums/meta-data-field.enum';
import { Company } from 'src/app/core/models/company';
import { SalesVelocitySettingsType } from '@stockaid/shared-enums';
import { getContrastYIQ } from '@stockaid/utils';
import {
  COMMON_FORMAT_DATE,
  FORMAT_PATTERN,
} from 'src/app/core/constants/format-date.constant';
import { CustomValidators } from '@stockaid/services';

@Component({
  selector: 'app-generic-item-modal',
  templateUrl: './generic-item-modal.component.html',
  styleUrls: ['./generic-item-modal.component.scss'],
})
export class GenericItemModalComponent<T>
  extends BaseComponent
  implements OnInit
{
  @Input() metadataFields: MetaDataField[];
  @Input() forbiddenFields: string[] = [];
  @Input() item: any;
  @Input() currentCompany: Company;
  @Input() modalTitle: string;
  @Input() resourceService: ResourceService<T>;
  @Input() keyName: string = 'key';

  readonly DisplayControlInput = DisplayControlInput;
  readonly getContrastYIQ = getContrastYIQ;
  readonly FORMAT_DATE = COMMON_FORMAT_DATE;
  readonly FORMAT_PATTERN = FORMAT_PATTERN;

  genericItemForm: FormGroup;

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private snotifyService: SnotifyService,
    private restockSuggestionService: RestockSuggestionService
  ) {
    super();
  }

  ngOnInit(): void {
    this.genericItemForm = new FormGroup({});
    const syncedFields = this.item?.syncedFields || [];
    this.metadataFields = this.metadataFields
      .filter((item) => !syncedFields.find((m) => m.field === item.field))
      .concat(syncedFields);

    this.metadataFields.forEach((m) => {
      if (this.forbiddenFields.includes(m?.field)) {
        return;
      }

      this.buildFormControl(m);
    });
    this.genericItemForm.setValidators([
      CustomValidators.sumControlsLessThanAmount(
        ['leadTime', 'orderInterval'],
        365,
        this.metadataFields
      ),
    ]);

    asapScheduler.schedule(() => {
      this.genericItemForm.patchValue(this.item);
    });
  }

  saveItem() {
    for (const key of Object.keys(this.genericItemForm.controls)) {
      if (
        this.genericItemForm.get(key).dirty &&
        [
          ItemField.itemHistoryLength,
          ItemField.fbaFee,
          ItemField.inboundFcProcessing,
          ItemField.inbound,
          ItemField.inboundAwd,
          ItemField.awdInventoryQty,
          ItemField.isCustom,
          ItemField.rank,
          ItemField.average7DayPrice,
          ...(this.item?.syncedFields || []),
        ].includes(key)
      ) {
        return;
      }
    }

    this.item.doNotRestock = this.genericItemForm.value.doNotRestock;
    this.item.doNotOrder = this.genericItemForm.value.doNotOrder;
    this.item.isHidden = this.item.doNotRestock && this.item.doNotOrder;
    this.resourceService
      .save(
        { ...this.item, ...this.genericItemForm.getRawValue() },
        this.keyName
      )
      .pipe(
        this.autoCleanUp(),
        switchMap(() => {
          if (!this.item.doNotRestock) {
            return this.restockSuggestionService.getByIdWithType(
              this.item.key,
              SalesVelocitySettingsType.restockAMZ,
              this.currentCompany?.marketplaceId,
              this.currentCompany?.currencyCode
            );
          }

          return of(null);
        })
      )
      .subscribe((restockSuggestion) => {
        this.snotifyService.success('Save item successfully');
        this.activeModal.close(restockSuggestion);
      });
  }

  getMin(col: MetaDataField): number {
    const minMaxValidator = col.validators?.find((v: IFieldValidator) => {
      return v.type === FieldValidatorType.MinMax;
    });

    let min = Number.MIN_VALUE;

    if (minMaxValidator) {
      min = (minMaxValidator as MinMaxValidator).min;
    }

    return min;
  }

  getMax(col: MetaDataField): number {
    const minMaxValidator = col.validators?.find((v: IFieldValidator) => {
      return v.type === FieldValidatorType.MinMax;
    });

    let max = Number.MAX_VALUE;

    if (minMaxValidator) {
      max = (minMaxValidator as MinMaxValidator).max;
    }

    return max;
  }

  private extractValidators(mdf: MetaDataField): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (!mdf.validators || mdf.validators.length === 0) {
      // case name input validators
      if (mdf.field === 'name') {
        validators.push(Validators.required);
      }
      return validators;
    }
    //loop through validators array, and add to form field.
    mdf.validators.forEach((validator: IFieldValidator) => {
      switch (validator.type) {
        case FieldValidatorType.MinMax: {
          let minMax = validator as MinMaxValidator;
          validators.push(Validators.min(minMax.min));
          validators.push(Validators.max(minMax.max));
          break;
        }
        case FieldValidatorType.Pattern: {
          let pattern = validator as CustomPatternValidator;
          validators.push(Validators.pattern(pattern.pattern));
          break;
        }
        case FieldValidatorType.Email:
          validators.push(Validators.email);
          break;
        case FieldValidatorType.LessMore: {
          const lessMoreVali = validator as LessMoreValidator;
          validators.push(lessMoreVali.condition);
          break;
        }
      }
    });

    return validators;
  }

  private getIsDisabledStatus(mdf: MetaDataField) {
    const isDisabled = [
      ItemField.itemHistoryLength,
      ItemField.fbaFee,
      ItemField.inboundFcProcessing,
      ItemField.inbound,
      ItemField.inboundAwd,
      ItemField.awdInventoryQty,
      ItemField.isCustom,
      ItemField.rank,
      ItemField.average7DayPrice,
      ...(this.item?.syncedFields || []),
    ].includes(mdf.field as ItemField);
    return isDisabled;
  }

  private buildFormControl(mdf: MetaDataField) {
    const disabled = this.getIsDisabledStatus(mdf);
    const validators = this.extractValidators(mdf);

    switch (mdf.displayControl?.input) {
      case DisplayControlInput.Date:
        this.genericItemForm.addControl(
          mdf.field,
          this.fb.control({ value: null, disabled }, validators)
        );
        break;

      case DisplayControlInput.LookUp: {
        this.genericItemForm.addControl(
          mdf.field,
          this.fb.control({ value: '', disabled }, validators)
        );

        const lookupControl = mdf.displayControl as LookUpDisplayControl;
        //get the field that will hold the key
        const mapping = lookupControl?.lookUpConfig?.mapping?.find((x) => {
          return x.type === LookUpMappingType.Key;
        });
        if (mapping) {
          this.genericItemForm.addControl(
            mapping.to,
            this.fb.control({ value: '', disabled }, validators)
          );
        }
        break;
      }

      default:
        this.genericItemForm.addControl(
          mdf.field,
          this.fb.control({ value: '', disabled }, validators)
        );
        break;
    }
  }
}
